"use client";

import React from "react";
import { IoWatchOutline } from "react-icons/io5";

import { Badge, Card } from "@/components/ui";

import { formatDateTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { formatWearableProvider } from "@/lib/wearable-connect";

import {
  WearableBackfillState,
  WearableConnection,
  WearableConnectionStatus,
} from "@/types/wearable";

export const WEARABLE_STATUS_LABELS: Record<WearableConnectionStatus, string> =
  {
    connected: "Connected",
    disconnected: "Disconnected",
    // Said plainly rather than as a status word: the patient has to go into the
    // device's own app to undo this, and "revoked" alone does not say that.
    access_revoked: "Access turned off in the device's app",
    connection_error: "Needs reconnecting",
  };

export const WEARABLE_STATUS_VARIANTS: Record<
  WearableConnectionStatus,
  "success" | "neutral" | "warning"
> = {
  connected: "success",
  disconnected: "neutral",
  access_revoked: "warning",
  connection_error: "warning",
};

/**
 * Said in terms of the history, never of the device. A device whose earlier
 * months are still arriving is working perfectly, so this line must not read as a
 * fault — a clinician would disconnect a healthy device to fix it.
 *
 * One state, and the absent one is the point. There used to be a second line
 * here, "Some earlier readings may be missing", shown once a history request had
 * stopped short of the chunk count the aggregator announced. Histories now arrive
 * as objects swept from a bucket, and an object carries nothing naming the
 * request it answers, so nothing can be counted and nothing can be short. Keeping
 * the warning would mean showing it to every patient forever on evidence that no
 * longer exists — usually false, and quickly ignored.
 *
 * Hoisted to module scope so the object is not rebuilt on every render.
 */
const WEARABLE_BACKFILL_LABELS: Record<WearableBackfillState, string> = {
  importing: "Earlier readings can take a day to arrive",
};

const WEARABLE_BACKFILL_CLASSES: Record<WearableBackfillState, string> = {
  importing: "text-foreground-secondary",
};

export function isWearableConnectionDegraded(
  connection: WearableConnection,
): boolean {
  return connection.status !== "connected";
}

interface ConnectionCardProps {
  connection: WearableConnection;
  /**
   * What can be done to this connection, supplied by whichever surface is
   * rendering it. Deliberately a slot rather than `canDisconnect` /
   * `showReconnect` flags: this card is drawn in the portal and on the chart
   * with different actions, and every boolean added here would need a
   * precedence rule against the others. The card owns identity, status and
   * layout; the caller owns the affordances.
   */
  actions?: React.ReactNode;
  className?: string;
}

export default function ConnectionCard({
  connection,
  actions,
  className,
}: ConnectionCardProps) {
  return (
    <Card
      className={cx("flex items-center justify-between gap-4 p-5", className)}
    >
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background-secondary">
          <IoWatchOutline className="h-5 w-5 text-foreground-secondary" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-body-strong text-foreground">
            {formatWearableProvider(connection.provider)}
          </p>
          {connection.connectedAt ? (
            <p className="mt-0.5 text-caption text-foreground-secondary">
              Connected {formatDateTime(connection.connectedAt)}
            </p>
          ) : null}
          {/*
            A scope change is a second axis, not a status: the connection is
            still working and still says Connected, while one metric has quietly
            stopped arriving. Shown as its own line for exactly that reason — a
            badge would have to displace the status to be seen.
          */}
          {connection.scopesChangedAt ? (
            <p className="mt-0.5 text-caption text-status-warning-text">
              Some data sharing changed{" "}
              {formatDateTime(connection.scopesChangedAt)}
            </p>
          ) : null}
          {/*
            A third axis again, and the reason it gets its own line rather than
            a badge: the chart is a month deep the moment the connection appears,
            and grows to three once the next sweep lands. Someone reading a short
            trend in between should know more is coming rather than conclude the
            device only started working recently.
          */}
          {connection.backfill ? (
            <p
              className={cx(
                "mt-0.5 text-caption",
                WEARABLE_BACKFILL_CLASSES[connection.backfill],
              )}
            >
              {WEARABLE_BACKFILL_LABELS[connection.backfill]}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant={WEARABLE_STATUS_VARIANTS[connection.status]}>
          {WEARABLE_STATUS_LABELS[connection.status]}
        </Badge>
        {actions}
      </div>
    </Card>
  );
}
